import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Utensils, ArrowRight, Video, BookOpen } from "lucide-react";

export default function Admin() {
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState({});
  const [discord, setDiscord] = useState("");

  const load = async () => {
    try {
      const [m, s, c] = await Promise.all([api.get("/admin/members"), api.get("/admin/analytics"), api.get("/config")]);
      setMembers(m.data); setStats(s.data); setDiscord(c.data.discord_invite_url || "");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const saveDiscord = async () => {
    try { await api.put("/config/discord", { invite_url: discord }); toast.success("Discord link updated."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="label-eyebrow">Admin</div>
        <h1 className="wordmark mt-3 text-4xl md:text-5xl uppercase">Members & Analytics.</h1>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["Members", stats.total_users],
          ["Active", stats.active_entitlements],
          ["Workouts Done", stats.workouts_completed],
          ["Activation", `${stats.activation_rate ?? 0}%`],
        ].map(([k, v]) => (
          <div key={k} className="bg-card border border-border rounded-lg p-5">
            <div className="wordmark text-4xl">{v ?? "—"}</div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1">{k}</div>
          </div>
        ))}
      </div>

      <div className="bg-card border border-border rounded-lg p-6" data-testid="admin-funnel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="label-eyebrow">$59 Funnel · last 30 days</div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
            Conversion {(() => { const f = stats.funnel_30d || {}; const v = f.view_10k30_page || 0; return v ? `${((f.purchase_membership || 0) / v * 100).toFixed(1)}%` : "—"; })()} view → purchase
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            ["view_10k30_page", "Viewed 10K30"],
            ["click_join_ba30", "Clicked Join"],
            ["view_join_page", "Viewed /join"],
            ["begin_checkout", "Began Checkout"],
            ["purchase_membership", "Purchased"],
          ].map(([k, label], i) => (
            <div key={k} className="rounded-md border border-border p-4" data-testid={`funnel-${k}`}>
              <div className="text-[10px] tracking-widest text-muted-foreground">0{i + 1}</div>
              <div className="wordmark text-3xl mt-1">{stats.funnel_30d?.[k] ?? "—"}</div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
            </div>
          ))}
        </div>
        {(stats.purchase_sources_30d?.length > 0 || Object.keys(stats.pathway_waitlist || {}).length > 0) && (
          <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-xs text-muted-foreground">
            {stats.purchase_sources_30d?.length > 0 && <span>Purchases by source: {stats.purchase_sources_30d.map((s) => `${s.source} ${s.n}`).join(" · ")}</span>}
            {Object.keys(stats.pathway_waitlist || {}).length > 0 && <span>Pathway waitlist: {Object.entries(stats.pathway_waitlist).map(([k, v]) => `${k} ${v}`).join(" · ")}</span>}
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <Link to="/app/admin/meals" className="bg-foreground text-background rounded-lg p-6 hover:bg-primary transition-colors group" data-testid="admin-cms-meals">
          <Utensils className="text-primary group-hover:text-primary-foreground" size={22} strokeWidth={2.25} />
          <div className="mt-4 label-eyebrow !text-background/70 group-hover:!text-primary-foreground/80">Content Management</div>
          <div className="mt-2 font-head uppercase text-xl">Meals CMS</div>
          <div className="mt-3 text-sm text-background/80 group-hover:text-primary-foreground/90 inline-flex items-center gap-2">Manage the 40-meal library <ArrowRight size={14} /></div>
        </Link>
        <Link to="/app/admin/site-videos" className="bg-foreground text-background rounded-lg p-6 hover:bg-primary transition-colors group" data-testid="admin-cms-site-videos">
          <Video className="text-primary group-hover:text-primary-foreground" size={22} strokeWidth={2.25} />
          <div className="mt-4 label-eyebrow !text-background/70 group-hover:!text-primary-foreground/80">Homepage CMS</div>
          <div className="mt-2 font-head uppercase text-xl">Our System Works · Videos</div>
          <div className="mt-3 text-sm text-background/80 group-hover:text-primary-foreground/90 inline-flex items-center gap-2">Manage credible-source reels <ArrowRight size={14} /></div>
        </Link>
        <Link to="/app/admin/lessons" className="bg-foreground text-background rounded-lg p-6 hover:bg-primary transition-colors group" data-testid="admin-cms-lessons">
          <BookOpen className="text-primary group-hover:text-primary-foreground" size={22} strokeWidth={2.25} />
          <div className="mt-4 label-eyebrow !text-background/70 group-hover:!text-primary-foreground/80">Curriculum CMS</div>
          <div className="mt-2 font-head uppercase text-xl">Longevity Lessons</div>
          <div className="mt-3 text-sm text-background/80 group-hover:text-primary-foreground/90 inline-flex items-center gap-2">Edit 12 lessons · attach founder videos <ArrowRight size={14} /></div>
        </Link>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <div className="label-eyebrow">Discord Invite Link</div>
        <div className="mt-3 flex gap-2">
          <input value={discord} onChange={(e) => setDiscord(e.target.value)} className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="admin-discord-input" />
          <button onClick={saveDiscord} className="btn-primary btn-sm" data-testid="admin-discord-save">Save</button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <div className="label-eyebrow mb-3">Members</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Email</th><th className="py-2 pr-4">Role</th>
                <th className="py-2 pr-4">Badge</th><th className="py-2 pr-4">Track</th><th className="py-2 pr-4">Onboarded</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className="border-b border-border/60" data-testid={`admin-row-${m.id}`}>
                  <td className="py-2 pr-4">{m.name}</td><td className="py-2 pr-4">{m.email}</td>
                  <td className="py-2 pr-4">{m.role}</td><td className="py-2 pr-4">{m.badge || "—"}</td>
                  <td className="py-2 pr-4">{m.track || "—"}</td><td className="py-2 pr-4">{m.onboarded ? "✓" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
